import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Select from "react-select";

import Button from "../Button";
import TextInput from "../TextInput";
import { showToast } from "../../utils/toast";
import { useInviteUser } from "../../services/queries/users";
import { UserRole } from "../../services/queries/users/types";

const schema = yup.object().shape({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  role: yup
    .mixed<UserRole>()
    .oneOf(Object.values(UserRole), "Role is required")
    .required("Role is required"),
});

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
}

interface AddUserFormProps {
  businessId: string;
  onClose: () => void;
}

export default function AddUserForm({ businessId, onClose }: AddUserFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const { mutate: inviteUser, isLoading } = useInviteUser();

  const handleFormSubmit = async (formData: FormValues) => {
    inviteUser(
      {
        businessId,
        data: formData,
      },
      {
        onSuccess: () => {
          showToast("User invited successfully", "success");
          onClose();
          reset(); // Reset form on successful submission
        },
        onError: () => {
          showToast("Failed to invite user", "error");
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="form-field">
        <label className="block text-gray-700 text-sm font-medium mb-1">
          First Name
        </label>

        <TextInput
          {...register("firstName")}
          error={errors.firstName?.message}
          placeholder="John"
        />
      </div>

      <div className="form-field">
        <label className="block text-gray-700 text-sm font-medium mb-1">
          Last Name
        </label>

        <TextInput
          {...register("lastName")}
          error={errors.lastName?.message}
          placeholder="Doe"
        />
      </div>

      <div className="form-field">
        <label className="block text-gray-700 text-sm font-medium mb-1">
          Email
        </label>

        <TextInput
          type="email"
          {...register("email")}
          error={errors.email?.message}
          placeholder="john.doe@example.com"
        />
      </div>

      <div className="form-field">
        <label className="block text-gray-700 text-sm font-medium mb-1">
          Role
        </label>

        <Controller
          name="role"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange } }) => (
            <Select
              options={Object.values(UserRole).map((role) => ({
                value: role,
                label: role.toUpperCase(),
              }))}
              onChange={(newValue) => onChange(newValue?.value)}
            />
          )}
        />

        {errors.role && (
          <p className="text-red-500 text-xs italic">{errors.role?.message}</p>
        )}
      </div>

      <div className="form-actions flex justify-end space-x-3 pt-4">
        <Button type="button" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>

        <Button variant="primary" type="submit" loading={isLoading}>
          Invite User
        </Button>
      </div>
    </form>
  );
}
